import { Phone, Mail } from 'lucide-react';
import {
	WHATSAPP_NUMBER_FORMATTED,
	WHATSAPP_TEL_LINK,
} from '@/constants/whatsapp';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useGetBlogPosts } from '@/services/queries/blog';

const Footer = () => {
	const logoProfessional =
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986601/Plumaa_H_psjyrs.png';
	const navigate = useNavigate();
	const location = useLocation();

	const { data: blogData } = useGetBlogPosts({
		page: 1,
		limit: 3,
		sort: 'created_at',
		sort_direction: 'desc',
	});

	const handleScrollToHelp = () => {
		const element = document.getElementById('central-ajuda');

		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
			return;
		}

		const fallbackPath =
			location.pathname === '/empresa' ||
			location.pathname.startsWith('/empresa/')
				? '/empresa'
				: location.pathname === '/pacientes'
				  ? '/pacientes'
				  : location.pathname === '/colaboradores'
				    ? '/colaboradores'
				    : '/';

		if (location.pathname !== fallbackPath) {
			navigate(fallbackPath);
			setTimeout(() => {
				document.getElementById('central-ajuda')?.scrollIntoView({
					behavior: 'smooth',
				});
			}, 100);
		}
	};

	return (
		<footer className="w-full border-t border-zinc-100 bg-white px-4 pb-8 pt-16">
			<div className="mx-auto max-w-7xl">
				{/* GRID PRINCIPAL */}
				<div className="mb-16 grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
					{/* COLUNA 1: LOGO E DESCRIÇÃO */}
					<div className="space-y-6">
						<img
							src={logoProfessional}
							alt="Plumaa"
							className="h-8 w-auto"
						/>
						<p className="max-w-xs text-sm leading-relaxed text-zinc-500">
							Transformando o acesso à saúde mental no Brasil com
							tecnologia, cuidado humano e ética profissional.
						</p>
					</div>

					{/* COLUNA 2: Blog Plumaa */}
					<div className="flex flex-col space-y-4">
						<h6 className="font-bold text-zinc-900">
							<Link
								to="/blog"
								className="transition-colors hover:text-brand-pink"
							>
								Blog Plumaa
							</Link>
						</h6>
						<div className="flex flex-col space-y-3">
							{blogData?.data.map((post) => (
								<Link
									key={post.id}
									to={`/blog/${encodeURIComponent(
										post.slug,
									)}`}
									className="line-clamp-2 flex items-start gap-2 text-sm leading-snug text-zinc-500 transition-colors hover:text-zinc-900"
								>
									<span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-zinc-300" />
									{post.title}
								</Link>
							))}
							{(!blogData || blogData.data.length === 0) && (
								<Link
									to="/blog"
									className="text-sm text-zinc-500 transition-colors hover:text-zinc-900"
								>
									Ver todos os artigos
								</Link>
							)}
						</div>
					</div>

					{/* COLUNA 3: SUPORTE */}
					<div className="flex flex-col space-y-4">
						<h6 className="font-bold text-zinc-900">Suporte</h6>
						<div className="flex flex-col space-y-3">
							<button
								onClick={handleScrollToHelp}
								className="text-left text-sm text-zinc-500 transition-colors hover:text-zinc-900"
							>
								Central de Ajuda
							</button>
							<Link
								to="/politica-de-privacidade"
								className="text-sm text-zinc-500 transition-colors hover:text-zinc-900"
							>
								Privacidade
							</Link>
							<Link
								to="/termos-de-uso"
								className="text-sm text-zinc-500 transition-colors hover:text-zinc-900"
							>
								Termos de Uso
							</Link>
						</div>
					</div>

					{/* COLUNA 4: CONTATO */}
					<div className="flex flex-col space-y-4">
						<h6 className="font-bold text-zinc-900">Contato</h6>
						<div className="flex flex-col space-y-4">
							<a
								href={WHATSAPP_TEL_LINK}
								className="group flex items-center gap-3 text-zinc-500 transition-all hover:text-zinc-900"
							>
								<div className="flex size-8 items-center justify-center rounded-lg bg-zinc-50 group-hover:bg-zinc-100">
									<Phone className="size-4 text-stone-600" />
								</div>
								<span className="text-sm">
									{WHATSAPP_NUMBER_FORMATTED}
								</span>
							</a>
							<a
								href="mailto:contato@plumaa.com.br"
								className="group flex items-center gap-3 text-zinc-500 transition-all hover:text-zinc-900"
							>
								<div className="flex size-8 items-center justify-center rounded-lg bg-zinc-50 group-hover:bg-zinc-100">
									<Mail className="size-4 text-stone-600" />
								</div>
								<span className="text-sm">
									contato@plumaa.com.br
								</span>
							</a>
						</div>
					</div>
				</div>

				{/* DIVISOR E COPYRIGHT */}
				<div className="flex flex-col items-center justify-between gap-4 border-t border-zinc-100 pt-8 md:flex-row">
					<p className="text-xs text-zinc-400">
						© {new Date().getFullYear()} Plumaa. Todos os direitos
						reservados.
					</p>
				</div>
			</div>
		</footer>
	);
};

export { Footer };
